<template>
  <BaseModal
    v-if="$page.props.shared.profilingWizard"
    :name="USER_POST_PURCHASE_PROFILING_WIZARD_MODAL_NAME"
    :click-outside-to-close="false"
    width-class="max-w-[750px]"
    is-background-blurred
    :auto-focus="false"
  >
    <img
      src="@/assets/images/wizard-flowers.png"
      alt=""
      class="w-full rounded-t-xl"
    />
    <div class="rounded-xl p-8">
      <div class="text-center">
        <div class="heading-1">
          <div>Hi {{ currentUser?.firstName }},</div>
          <div>Welcome to GrowthMentor!</div>
        </div>
        <div class="mt-2 text-sm text-neutral-600">
          Let's tailor your experience with a quick profile updates.
        </div>
        <BaseButton
          @click="handleClickCustomizeExperience"
          color="primary"
          class="mt-6"
        >
          Customize Experience
          <ChevronRightIcon class="ml-2" />
        </BaseButton>
      </div>
    </div>
  </BaseModal>

  <BaseModal
    v-if="$page.props.shared.profilingWizard"
    :name="USER_PROFILING_WIZARD_MODAL_NAME"
    :click-outside-to-close="false"
    width-class="max-w-[750px]"
    is-background-blurred
    :auto-focus="false"
  >
    <div class="relative h-2 w-full overflow-hidden rounded-t-2xl">
      <div class="absolute inset-x-0 top-0 h-full w-full bg-neutral-200" />
      <div
        class="bg-secondary-500 absolute inset-x-0 top-0 h-full"
        :style="{ width: percentProgressComplete }"
      />
    </div>
    <div class="rounded-xl p-4 sm:p-14">
      <div class="text-center">
        <div class="heading-1">{{ profilingWizard.title }}</div>
        <div
          v-if="profilingWizard.description"
          class="mt-2 text-sm text-neutral-600"
        >
          {{ profilingWizard.description }}
        </div>
      </div>
      <div class="mt-6">
        <!--Text field-->
        <div v-if="activeQuestion.kind === 'text_field'">
          <FormInput v-model="form.singularAnswer" type="text">
            <template #label>{{ activeQuestion.question }}</template>
          </FormInput>
        </div>

        <!--Text area-->
        <div v-if="activeQuestion.kind === 'text_area'">
          <FormInput as="textarea" v-model="form.singularAnswer" rows="3">
            <template #label>{{ activeQuestion.question }}</template>
          </FormInput>
        </div>

        <!--Single select-->
        <div v-if="activeQuestion.kind === 'single_select'">
          <FormRadioGroup
            v-model="form.singularAnswer"
            :options="activeQuestion.options"
            button-classes="w-full p-3"
            theme-color="primary"
          >
            <template #label>{{ activeQuestion.question }}</template>
            <template #default="{ option }">
              <div class="flex items-center space-x-4">
                <img
                  v-if="option.icon"
                  :src="option.icon"
                  alt=""
                  class="h-12 w-12"
                />
                <div class="text-left">
                  <div class="font-semibold">{{ option.label }}</div>
                  <div class="mt-1 text-xs text-neutral-500">
                    {{ option.description }}
                  </div>
                </div>
              </div>
            </template>
          </FormRadioGroup>
        </div>

        <!--Multiselect-->
        <div v-if="activeQuestion.kind === 'multiselect'">
          <FormCheckboxGroup
            v-model="form.multipleAnswer"
            :options="activeQuestion.options"
            button-classes="w-full p-3"
            theme-color="primary"
          >
            <template #label>{{ activeQuestion.question }}</template>
            <template #default="{ option }">
              <div class="flex items-center space-x-4">
                <img
                  v-if="option.icon"
                  :src="option.icon"
                  alt=""
                  class="h-12 w-12"
                />
                <div class="text-left">
                  <div class="font-semibold">{{ option.label }}</div>
                  <div class="mt-1 text-xs text-neutral-500">
                    {{ option.description }}
                  </div>
                </div>
              </div>
            </template>
          </FormCheckboxGroup>
        </div>

        <!--Dropdown and roles-->
        <div v-if="['dropdown', 'roles'].includes(activeQuestion.kind)">
          <FormSelect
            v-model="form.singularAnswer"
            :options="activeQuestion.options"
            searchable
          >
            <template #label>{{ activeQuestion.question }}</template>
          </FormSelect>
        </div>

        <!--Industries-->
        <div v-if="activeQuestion.kind === 'industries'">
          <FormSelectWithGroups
            v-model="form.multipleAnswer"
            :groups="activeQuestion.options"
            multiple
          >
            <template #label>{{ activeQuestion.question }}</template>
          </FormSelectWithGroups>
        </div>
      </div>
      <div class="mt-6 text-right">
        <BaseButton
          @click="submitAnswer"
          color="outline-gray"
          :is-disabled="!canSubmitForm || form.processing"
        >
          {{ submitButtonLabel }}
          <LoadingCircle v-if="form.processing" size="12" class="ml-2" />
        </BaseButton>
      </div>
    </div>
  </BaseModal>

  <BaseModal
    :name="POST_PURCHASE_FINAL_MODAL_NAME"
    :click-outside-to-close="false"
    width-class="max-w-[750px]"
    is-background-blurred
    :auto-focus="false"
  >
    <img
      src="@/assets/images/someone-sitting-on-carpet.png"
      alt=""
      class="mx-auto h-[400px]"
    />
    <div class="rounded-xl p-8">
      <div class="text-center">
        <div class="heading-1">All done! Thank you</div>
        <div class="mt-2 text-sm text-neutral-600">
          We'll help you build better session experience.
        </div>
        <BaseButton
          @click="closeModal(POST_PURCHASE_FINAL_MODAL_NAME)"
          color="outline-gray"
          class="mt-6"
        >
          Close
        </BaseButton>
      </div>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import BaseModal from '@/shared/components/BaseModal.vue'
import ChevronRightIcon from '~icons/lucide/chevron-right'
import { useForm, usePage } from '@inertiajs/vue3'
import { computed } from 'vue'
import FormInput from '@/shared/components/forms/FormInput.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import LoadingCircle from '@/shared/components/LoadingCircle.vue'
import { useModals } from '@/shared/composables/useModals.js'
import { notify } from '@kyvg/vue3-notification'
import FormRadioGroup from '@/shared/components/forms/FormRadioGroup.vue'
import FormCheckboxGroup from '@/shared/components/forms/FormCheckboxGroup.vue'
import FormSelect from '@/shared/components/forms/FormSelect.vue'
import FormSelectWithGroups from '@/shared/components/forms/FormSelectWithGroups.vue'
import {
  USER_POST_PURCHASE_PROFILING_WIZARD_MODAL_NAME,
  USER_PROFILING_WIZARD_MODAL_NAME,
} from '@/shared/data/globalVariables.js'
import { useCurrentUser } from '@/shared/composables/useCurrentUser'

type ProfilingWizard = {
  title: string
  description: string
  id: number
  kind: 'progressive' | 'post_purchase'
  questionsTotal: number
  questions: {
    id: number
    kind:
      | 'text_field'
      | 'text_area'
      | 'single_select'
      | 'multiselect'
      | 'dropdown'
      | 'industries'
      | 'roles'
    mandatory: boolean
    options:
      | { label: string; value: string }[]
      | { name: string; options: { label: string; value: string }[] }[]
    question: string
  }[]
}

const page = usePage()
const { closeModal, openModal } = useModals()
const { currentUser } = useCurrentUser()
const POST_PURCHASE_FINAL_MODAL_NAME = 'post-purchase-final-modal'

const profilingWizard = computed(
  () => page.props.shared.profilingWizard as ProfilingWizard,
)
const percentProgressComplete = computed(() => {
  const total = profilingWizard.value.questionsTotal
  const remaining =
    profilingWizard.value.questionsTotal -
    profilingWizard.value.questions.length
  return `${Math.round((remaining / total) * 100)}%`
})
const hasNextQuestion = computed(
  () => profilingWizard.value.questions.length > 1,
)
const submitButtonLabel = computed(() => {
  if (!activeQuestion.value.mandatory) return 'Skip'
  return hasNextQuestion.value ? 'Next' : 'Submit'
})

const form = useForm({
  singularAnswer: '',
  multipleAnswer: [],
})
const canSubmitForm = computed(() => {
  const isInputFilled = !!form.singularAnswer || form.multipleAnswer.length > 0
  return isInputFilled || !activeQuestion.value.mandatory
})

const activeQuestion = computed(() => profilingWizard.value.questions[0])

const submitAnswer = () => {
  const singularAnswerKinds: ProfilingWizard['questions'][0]['kind'][] = [
    'text_field',
    'text_area',
    'single_select',
    'dropdown',
    'roles',
  ]

  const wizardKind = profilingWizard.value.kind

  form
    .transform((data) => {
      return singularAnswerKinds.includes(activeQuestion.value.kind)
        ? { answers: [data.singularAnswer] }
        : {
            answers: data.multipleAnswer,
          }
    })
    .post(`/profiling-wizard/questions/${activeQuestion.value.id}/answers`, {
      preserveScroll: true,
      onSuccess: (response) => {
        const shared = response.props.shared as {
          profilingWizard: ProfilingWizard | null
        }
        if (shared.profilingWizard) {
          form.reset()
        } else {
          if (wizardKind === 'post_purchase') {
            closeModal(USER_PROFILING_WIZARD_MODAL_NAME)
            openModal(POST_PURCHASE_FINAL_MODAL_NAME)
          } else {
            closeModal(USER_PROFILING_WIZARD_MODAL_NAME)
            notify({
              type: 'success',
              text: 'Thank you! Your answers have been submitted.',
            })
          }
        }
      },
    })
}

const handleClickCustomizeExperience = () => {
  closeModal(USER_POST_PURCHASE_PROFILING_WIZARD_MODAL_NAME)
  openModal(USER_PROFILING_WIZARD_MODAL_NAME)
}
</script>
